'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { cn } from '@/shared/lib/cn';
import { QuickFeedbackComposer } from './QuickFeedbackComposer';

/**
 * Always-available "Feedback" header action for wide screens (tablet / desktop).
 *
 * It is the SAME support-ticket mechanism as the full form in Account →
 * Feedback (`FeedbackForm` → `supportService.submitFeedback`), reduced to the
 * one field that matters for a drive-by remark: the message. The category is
 * pinned to `general_inquiry` and attachments are omitted — the full form stays
 * the place for a categorized report with screenshots.
 *
 * Hidden below `md` on purpose: on phones the header has no room for a labelled
 * action, so the same composer is offered as an item in the page's ⋮ menu
 * (`FeedbackQuickDialog`). Mirrors Apple's `feedbackToolbarItem()` and
 * Android's `FeedbackTopBarAction()`.
 */
export function FeedbackHeaderButton({
  variant = 'default',
  className,
}: {
  /**
   * `hero` renders the dark-glass pill used over the building page's teal wash,
   * matching the ⋮ button it sits next to (see `ProjectActionsMenu`).
   */
  variant?: 'default' | 'hero';
  className?: string;
}) {
  const t = useTranslations('feedback');
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Wide screens only — see the component doc.
          className={cn(
            'hidden md:inline-flex h-9 text-toolbar-action',
            variant === 'hero' &&
              'bg-background/60 backdrop-blur-sm border-transparent text-foreground hover:bg-background/80',
            className
          )}
        >
          <MessageSquare className="h-4 w-4" />
          {t('quick.button')}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-3">
        <QuickFeedbackComposer onSent={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
